<template>
  <div class="selector-estructura">
    <p class="instruccion">
      Pulse la <span class="x-ejemplo">✕</span> del elemento que
      <strong>NO desea incluir</strong>. Al deshabilitar un componente también se deshabilitan sus hijos.
      Arrastre el ícono <strong>⠿</strong> para cambiar el orden de los {{ nivelesArray[0]?.toLowerCase() ?? 'componentes' }} de primer nivel.
    </p>

    <!-- Filtros -->
    <div class="filtro-botones">
      <button
        :class="['btn-filtro', tipoFiltro === 'todos' ? 'activo' : '']"
        @click="tipoFiltro = 'todos'; componenteNivel1Filtro = null"
      >Mostrar todo</button>
      <button
        :class="['btn-filtro', tipoFiltro === 'por-componente' ? 'activo' : '']"
        @click="tipoFiltro = 'por-componente'"
      >Por {{ nivelesArray[0] ?? 'Componente' }}</button>
    </div>
    <div v-if="tipoFiltro === 'por-componente'" class="filtro-select-wrap">
      <select v-model.number="componenteNivel1Filtro" class="filtro-select-ctrl">
        <option :value="null">— Selecciona —</option>
        <option v-for="c in componentesNivel1Sorted" :key="c.id_componente" :value="c.id_componente">
          {{ c.ordinal }} – {{ c.nombre }}
        </option>
      </select>
    </div>

    <div class="arbol-contenedor">
      <div
        v-for="nodo in nodosVisuales"
        :key="`comp-${nodo.compId}`"
        class="nodo-fila nodo-componente"
        :class="{
          'nodo-excluido': estaExcluido(nodo),
          'nodo-arrastrando': nodo.nivel === 0 && draggingCompId === nodo.compId,
          'drop-before': nodo.nivel === 0 && dropTargetCompId === nodo.compId && dropSide === 'before',
          'drop-after': nodo.nivel === 0 && dropTargetCompId === nodo.compId && dropSide === 'after',
        }"
        :style="estiloNodo(nodo)"
        :draggable="nodo.nivel === 0 && arbol.length > 1"
        @dragstart.stop="nodo.nivel === 0 && onDragStart(nodo.compId, $event)"
        @dragover.stop.prevent="nodo.nivel === 0 && onDragOver(nodo.compId, $event)"
        @dragend="onDragEnd"
        @drop.stop.prevent="nodo.nivel === 0 && onDrop(nodo.compId)"
      >
        <span
          class="nivel-label"
          :class="{ 'nivel-excluido': estaExcluido(nodo) }"
        >{{ obtenerNombreNivel(nodo) }}</span>

        <span
          class="nodo-nombre"
          :style="{ paddingLeft: `${nodo.nivel * 20}px` }"
        ><span
          v-if="nodo.nivel === 0 && arbol.length > 1"
          class="drag-handle"
          title="Arrastra para reordenar"
          @click.stop
        >⠿</span>{{ nodo.nombre }}</span>

        <button
          class="btn-excluir"
          :class="{ excluido: estaExcluido(nodo) }"
          :title="estaExcluido(nodo) ? 'Incluir este elemento' : 'Excluir este elemento'"
          @click.stop="toggleNodo(nodo)"
        >✕</button>
      </div>

      <p v-if="nodosVisuales.length === 0" class="sin-datos">
        No se encontraron componentes en este modelo.
      </p>
    </div>
  </div>
</template>

<script lang="ts" setup>
/**
 * Selector de nodos del árbol, para filtrar o clonar por ordinal.
 *
 * Componente de presentación. Recibe sus datos por props o del store del módulo.
 */

import { ref, computed, onMounted } from 'vue'
import { useComponentes } from '@/store/GestionComponentes'
import useModelo from '@/store/GestionModelo'
import { limpiarNiveles } from '@/utils/nivelesHelper'
import type Componente from '@/interfaces/Componente'

interface NodoPlano {
  tipo: 'componente'
  nivel: number
  nombre: string
  compId: number
  ordinal: string
  tieneHijos: boolean
  rootCompId: number
}

const props = defineProps<{
  idModelo: number | null
}>()

const emit = defineEmits<{
  (e: 'update:cargando', value: boolean): void
}>()

const componentesStore = useComponentes()
const modeloStore = useModelo()

const arbol = ref<Componente[]>([])
const nivelesArray = ref<string[]>([])
const excluidos = ref(new Set<number>())
const idRaizComp = ref<number | null>(null)
const tipoFiltro = ref<'todos' | 'por-componente'>('todos')
const componenteNivel1Filtro = ref<number | null>(null)
const draggingCompId = ref<number | null>(null)
const dropTargetCompId = ref<number | null>(null)
const dropSide = ref<'before' | 'after'>('after')

function obtenerNombreNivel(nodo: NodoPlano): string {
  return nivelesArray.value[nodo.nivel] ?? `Nivel ${nodo.nivel + 1}`
}

const nodosPlanos = computed<NodoPlano[]>(() => {
  const result: NodoPlano[] = []

  function aplanar(comps: Componente[], nivel: number, rootCompId?: number) {
    const sorted = nivel === 0
      ? [...comps]
      : [...comps].sort((a, b) =>
          (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
        )
    for (const comp of sorted) {
      const tieneHijos = (comp.children?.length ?? 0) > 0
      const thisRootId = nivel === 0 ? comp.id_componente! : rootCompId!
      result.push({
        tipo: 'componente',
        nivel,
        nombre: comp.nombre,
        compId: comp.id_componente!,
        ordinal: comp.ordinal ?? '',
        tieneHijos,
        rootCompId: thisRootId,
      })
      if (tieneHijos) aplanar(comp.children!, nivel + 1, thisRootId)
    }
  }

  aplanar(arbol.value, 0)
  return result
})

const componentesNivel1Sorted = computed(() =>
  [...arbol.value].sort((a, b) =>
    (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
  )
)

const nodosVisuales = computed<NodoPlano[]>(() => {
  if (tipoFiltro.value === 'por-componente' && componenteNivel1Filtro.value != null) {
    return nodosPlanos.value.filter(n => n.rootCompId === componenteNivel1Filtro.value)
  }
  return nodosPlanos.value
})

function onDragStart(compId: number, e: DragEvent) {
  draggingCompId.value = compId
  if (e.dataTransfer) e.dataTransfer.effectAllowed = 'move'
}

function onDragOver(compId: number, e: DragEvent) {
  if (draggingCompId.value == null || draggingCompId.value === compId) return
  dropTargetCompId.value = compId
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect()
  dropSide.value = e.clientY < rect.top + rect.height / 2 ? 'before' : 'after'
}

function onDragEnd() {
  draggingCompId.value = null
  dropTargetCompId.value = null
}

function onDrop(compId: number) {
  if (draggingCompId.value == null || draggingCompId.value === compId) {
    draggingCompId.value = null
    dropTargetCompId.value = null
    return
  }
  const draggedId = draggingCompId.value
  const side = dropSide.value
  draggingCompId.value = null
  dropTargetCompId.value = null

  const arr = [...arbol.value]
  const draggedIdx = arr.findIndex(c => c.id_componente === draggedId)
  const targetIdx = arr.findIndex(c => c.id_componente === compId)
  if (draggedIdx === -1 || targetIdx === -1) return

  const [draggedComp] = arr.splice(draggedIdx, 1)
  const newTargetIdx = arr.findIndex(c => c.id_componente === compId)
  arr.splice(side === 'before' ? newTargetIdx : newTargetIdx + 1, 0, draggedComp)
  arbol.value = arr
}

function estaExcluido(nodo: NodoPlano): boolean {
  return excluidos.value.has(nodo.compId)
}

function estiloNodo(nodo: NodoPlano): Record<string, string> {
  if (estaExcluido(nodo)) {
    const grosor = nodo.tieneHijos ? '5px' : '3px'
    return {
      backgroundColor: '#ffd5d5',
      borderLeft: `${grosor} solid #cc2200`,
      boxShadow: '0 1px 4px rgba(204,34,0,0.15)',
    }
  }
  const claridad = Math.min(97, 85 + nodo.nivel * 8)
  return {
    backgroundColor: `hsl(210, 60%, ${claridad}%)`,
    borderLeft: nodo.tieneHijos ? '5px solid #0e69a0' : '3px solid #8bb8d6',
    boxShadow: nodo.tieneHijos
      ? '0 2px 6px rgba(0,0,0,0.12)'
      : '0 1px 3px rgba(0,0,0,0.07)',
  }
}

function todosDescIds(comp: Componente): number[] {
  const ids: number[] = [comp.id_componente!]
  for (const h of (comp.children ?? [])) ids.push(...todosDescIds(h))
  return ids
}

function encontrarComp(id: number, lista: Componente[] = arbol.value): Componente | null {
  for (const c of lista) {
    if (c.id_componente === id) return c
    const found = encontrarComp(id, c.children ?? [])
    if (found) return found
  }
  return null
}

function toggleNodo(nodo: NodoPlano) {
  const comp = encontrarComp(nodo.compId)
  if (!comp) return
  const next = new Set(excluidos.value)
  const descIds = todosDescIds(comp)
  if (next.has(comp.id_componente!)) {
    descIds.forEach(id => next.delete(id))
  } else {
    descIds.forEach(id => next.add(id))
  }
  excluidos.value = next
}

async function cargarEstructura() {
  if (!props.idModelo) return
  emit('update:cargando', true)
  try {
    const [raiz, modelo] = await Promise.all([
      componentesStore.cargarRaizPorModelo(props.idModelo),
      modeloStore.getModeloById(props.idModelo),
    ])
    if (modelo?.niveles) nivelesArray.value = limpiarNiveles(modelo.niveles)
    if (!raiz?.id_componente) return
    idRaizComp.value = raiz.id_componente
    await componentesStore.cargarArbolDesdeRaiz(raiz.id_componente)
    const raizCompleta = componentesStore.componentes[0]
    arbol.value = [...(raizCompleta?.children ?? [])].sort((a, b) =>
      (a.ordinal ?? '').localeCompare(b.ordinal ?? '', undefined, { numeric: true })
    )
  } finally {
    emit('update:cargando', false)
  }
}

// Devuelve true si algún descendiente directo o indirecto está excluido
function tieneDescendienteExcluido(comp: Componente): boolean {
  for (const hijo of (comp.children ?? [])) {
    if (excluidos.value.has(hijo.id_componente!)) return true
    if (tieneDescendienteExcluido(hijo)) return true
  }
  return false
}

// Recoge el conjunto MÍNIMO de ordinales a enviar al backend.
// Si un componente y todos sus hijos están incluidos → se envía su ordinal (no se baja más).
// Si tiene algún descendiente excluido → se baja a sus hijos y se repite.
// Si él mismo está excluido → se omite por completo.
function colectarOrdinalesIncluidos(): string[] {
  const result: string[] = []

  function recolectar(comps: Componente[]): boolean {
    let aportaAlgo = false
    for (const comp of comps) {
      if (excluidos.value.has(comp.id_componente!)) continue
      if (tieneDescendienteExcluido(comp)) {
        const hijosAportaron = recolectar(comp.children ?? [])
        if (!hijosAportaron) {
          if (comp.ordinal) { result.push(comp.ordinal); aportaAlgo = true }
        } else {
          aportaAlgo = true
        }
      } else {
        if (comp.ordinal) { result.push(comp.ordinal); aportaAlgo = true }
      }
    }
    return aportaAlgo
  }

  recolectar(arbol.value)
  return result
}

// La estructura se carga al montar; el componente se monta al entrar al paso de selección.
onMounted(cargarEstructura)

// Expuesto al modal contenedor para que recoja la selección al confirmar.
defineExpose({ obtenerOrdinales: colectarOrdinalesIncluidos })
</script>

<style scoped>
.selector-estructura {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}

.instruccion {
  font-size: 0.86rem;
  color: #555;
  background: #fffbe6;
  border-left: 3px solid #eea222;
  padding: 0.5rem 0.75rem;
  border-radius: 4px;
  margin-bottom: 0.75rem;
  line-height: 1.5;
  flex-shrink: 0;
}

.x-ejemplo {
  display: inline-block;
  color: #cc2200;
  font-weight: 800;
  transform: rotate(-12deg);
}

.arbol-contenedor {
  border: 1px solid #dde3ea;
  border-radius: 6px;
  overflow-y: auto;
  max-height: 56vh;
  flex: 1;
  min-height: 0;
}

.sin-datos {
  padding: 1.5rem;
  text-align: center;
  color: #aaa;
  font-size: 0.85rem;
}

.nodo-fila {
  display: grid;
  grid-template-columns: 88px 1fr 36px;
  align-items: center;
  gap: 0.4rem;
  padding: 0.38rem 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  transition: background-color 0.15s ease, border-color 0.15s ease;
}

.nivel-label {
  font-size: 0.7rem;
  font-weight: 700;
  color: #0e69a0;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex-shrink: 0;
}

.nivel-excluido {
  color: #cc2200;
}

.nodo-nombre {
  word-break: break-word;
  line-height: 1.4;
}

.nodo-componente .nodo-nombre {
  font-weight: 600;
  font-size: 0.87rem;
  color: #1a1a1a;
}


.nodo-excluido .nodo-nombre {
  text-decoration: line-through;
  color: #9b1f0a;
  opacity: 0.85;
}

.btn-excluir {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: #bbb;
  font-size: 1.05rem;
  font-weight: 800;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(-13deg);
  transition: all 0.16s ease;
  padding: 0;
  line-height: 1;
}

.btn-excluir:hover {
  color: #cc2200;
  background: #ffe0dc;
  transform: rotate(-9deg) scale(1.2);
}

.btn-excluir.excluido {
  background: #cc2200;
  color: white;
  transform: rotate(-15deg) scale(1.12);
  box-shadow: 0 2px 8px rgba(204, 34, 0, 0.5);
}

.btn-excluir.excluido:hover {
  background: #a81a00;
  transform: rotate(-10deg) scale(1.18);
  box-shadow: 0 3px 10px rgba(204, 34, 0, 0.65);
}

.btn-excluir.heredado {
  background: #e05540;
  color: white;
  transform: rotate(-13deg);
  opacity: 0.7;
  cursor: default;
}

/* Filtros */
.filtro-botones {
  display: flex;
  gap: 0.4rem;
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
  flex-shrink: 0;
}

.btn-filtro {
  padding: 0.3rem 0.9rem;
  border-radius: 16px;
  border: 1px solid #ccc;
  background-color: #f9f9f9;
  cursor: pointer;
  font-size: 0.82rem;
  font-weight: 500;
  transition: all 0.18s ease;
  font-family: var(--font-family);
}

.btn-filtro:hover {
  background-color: #eea222;
  color: white;
}

.btn-filtro.activo {
  background-color: #0e69a0;
  color: white;
  border-color: #0e69a0;
}

.filtro-select-wrap {
  margin-bottom: 0.5rem;
  flex-shrink: 0;
}

.filtro-select-ctrl {
  width: 100%;
  padding: 0.4rem 0.7rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 0.85rem;
  font-family: var(--font-family);
  background-color: #f9f9f9;
  cursor: pointer;
}

.filtro-select-ctrl:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}

/* Drag-and-drop */
.drag-handle {
  display: inline-block;
  margin-right: 6px;
  color: #ccc;
  cursor: grab;
  font-size: 1rem;
  line-height: 1;
  transition: color 0.2s;
}

.nodo-fila:hover .drag-handle {
  color: #0e69a0;
}

.drag-handle:active {
  cursor: grabbing;
}

.nodo-arrastrando {
  opacity: 0.4;
}

.drop-before {
  border-top: 3px solid #0e69a0 !important;
}

.drop-after {
  border-bottom: 3px solid #0e69a0 !important;
}
</style>
